
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import GameCard from './GameCard'
import SkeletonLoading from './SkeletonLoading';

interface GameGridProps {
  selectedProvider: string;
}

export default function GameGrid({ selectedProvider }: GameGridProps) {

  const defaultVisibleCount = 42;

  const { user, saveGameUrl, allGames, setAllGames, isGamesLoaded, setIsGamesLoaded } = useAuth();
  const navigate = useNavigate();


  const [searchQuery, setSearchQuery] = useState('');
  const [isDisable, setIsDisable] = useState(false);
  const [visibleCount, setVisibleCount] = useState(defaultVisibleCount);

  useEffect(() => {
    if (!isGamesLoaded) {
      getGameList();
    }
  }, [isGamesLoaded]);

  // Reset visible count when search changes
  useEffect(() => {
    setVisibleCount(defaultVisibleCount);
  }, [searchQuery]);

  const getGameList = async () => {
    try {
      const response = await fetch("https://user-api.u-slot.vip/api/Game/get_game_list", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Get GameList Failed');
      }

      if (data.code === 0 && data.data) {
        setAllGames(JSON.parse(JSON.stringify(data.data)));
        setIsGamesLoaded(true);
      } else {
        throw new Error(data.message || 'Get GameList Failed 2');
      }
    } catch (err) {
      console.log(err instanceof Error ? err.message : 'An error occurred');
    }
  }

  const playGame = async (gameId: string) => {

    setIsDisable(true)
    if (!user) {
      setTimeout(() => setIsDisable(false), 100)
      navigate('/login');
      return
    }

    try {
      const response = await fetch("https://user-api.u-slot.vip/api/Game/game_play", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          id: gameId,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Play Game Failed!s');
      }

      if (data.message == "launch game success.") {
        saveGameUrl(data.data); // Your link here
        setIsDisable(false);
        navigate('/game-play');
      }
    } catch (err) {
      setIsDisable(false)
      console.log(err instanceof Error ? err.message : 'Play Game Failed!');
    }
  };

  // Filter games based on search query
  const filteredGames = allGames?.filter(game =>
    game.gameName.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  // Get visible games
  const displayGames = filteredGames.slice(0, visibleCount);

  // Check if there are more games to load
  const hasMore = visibleCount < filteredGames.length;

  // Handle load more
  const handleLoadMore = () => {
    setVisibleCount(prev => prev + defaultVisibleCount);
  };


  return (
    <section className="py-6 sm:py-8 md:py-12 lg:py-16 bg-gradient-to-b from-[#111319] to-[#16181D]" id="slots">
      <div className="max-w-7xl mx-auto px-4 sm:px-5 md:px-6 lg:px-12">
        <div className="text-center mb-6 sm:mb-8 md:mb-10">
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-2 md:mb-3" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            Featured Games
          </h2>
          <p className="text-white/70 text-xs sm:text-sm md:text-base" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Discover the most popular slots from top providers
          </p>
          <div className="flex gap-3">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search games by name..."
              className="flex-1 bg-[#0D0E11] border border-[#FFD349]/30 rounded-lg px-4 py-3 mt-4 text-white text-sm focus:outline-none focus:border-[#FFD349] transition-colors"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
              maxLength={20}
            />
          </div>
        </div>

        {allGames.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-3 md:gap-4 lg:gap-6">
            {displayGames.map((game) => (
              <div
                key={game.gameID}
                className="group relative bg-[#0D0E11] rounded-lg sm:rounded-xl overflow-hidden border border-[#29A5FF]/20 hover:border-[#EFBF65] hover:scale-104 transition-all duration-300 hover:shadow-[0px_6px_14px_rgba(0,0,0,0.45)]"
              >
                <GameCard isDisable={isDisable} game={game} playGame={playGame} />
              </div>
            ))}
          </div>) : (
          <SkeletonLoading />
        )}

        {/* Load More Button */}
        {
          hasMore && <div className="text-center mt-6 sm:mt-8 md:mt-10">
            <button onClick={handleLoadMore} className="bg-transparent border-2 border-[#EFBF65] hover:bg-[#EFBF65]/10 text-white font-bold px-5 sm:px-6 md:px-10 py-2 sm:py-2.5 md:py-3 rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer text-xs sm:text-sm md:text-base" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Load More Games({filteredGames.length - visibleCount} remaining)
            </button>
          </div>
        }
      </div>
    </section>
  );
}
